import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { Link } from 'react-router';
import type { ConnectionState, SimulateInboundResponse } from '@chatapp-inbox/shared';
import { isApiError, seedSimulation, simulateInbound } from '../../api';
import { invalidateConversationLists } from '../conversation';
import { lineNameWithPhone } from '../conversations';
import { Button } from '../details/Button';
import { StatusRegion } from '../details/StatusRegion';
import { useStatusMessage } from '../details/use-status-message';
import { NOT_DEMO_MESSAGE, adminErrorMessage } from './error-message';
import { Field, SelectField, TextAreaField } from './Field';
import { validateSimulation, type Errors, type SimulateField } from './validation';
import forms from './forms.module.css';
import section from './Section.module.css';
import styles from './SimulationSection.module.css';

/** "3 conversas criadas." / "1 conversa criada." / "As conversas de exemplo já existiam." */
export function seedSentence(created: number): string {
  if (created === 0) return 'As conversas de exemplo já existiam.';
  return created === 1 ? '1 conversa criada.' : `${created} conversas criadas.`;
}

/** Falha de uma rota de demonstração: 404 quer dizer que a instalação não é de demonstração. */
const simulationError = (error: unknown, fallback: string): string =>
  isApiError(error) && error.status === 404 ? NOT_DEMO_MESSAGE : adminErrorMessage(error, fallback);

/**
 * Ambiente de demonstração: simula mensagens de clientes e cria conversas de exemplo, sem um
 * WhatsApp de verdade. Só é montado quando a conexão diz `simulation` (o servidor também recusa
 * fora da demonstração). Usa os números LIGADOS: sem nenhum, não há por onde a mensagem entrar.
 */
export function SimulationSection({ connection }: { connection: ConnectionState }): ReactElement {
  const headingId = useId();
  const lines = connection.lines.filter((line) => line.enabled);

  return (
    <section aria-labelledby={headingId} className={section.card}>
      <h3 id={headingId} className={section.cardTitle}>
        Ambiente de demonstração
      </h3>
      <p className={forms.hint}>
        Isto só existe na demonstração: aqui você faz de conta que um cliente escreveu, sem usar um
        WhatsApp de verdade.
      </p>
      {lines.length === 0 ? (
        <p className={styles.empty}>
          Ligue ao menos um número na seção “Números” para simular mensagens.
        </p>
      ) : (
        <>
          <InboundForm lines={lines} />
          <SeedBlock />
        </>
      )}
    </section>
  );
}

function InboundForm({ lines }: { lines: ConnectionState['lines'] }): ReactElement {
  const headingId = useId();
  const [lineId, setLineId] = useState(lines[0]?.id ?? '');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [text, setText] = useState('');
  const [errors, setErrors] = useState<Errors<SimulateField>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<SimulateInboundResponse | null>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  // O número escolhido pode ter sido desligado com a tela aberta: vale o primeiro que sobrou.
  const selectedId = lines.some((line) => line.id === lineId) ? lineId : (lines[0]?.id ?? '');

  const send = useMutation({ mutationFn: simulateInbound });
  const pending = send.isPending;

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (pending) return;
    const found = validateSimulation({ name, phone, text });
    setErrors(found);
    setFormError(null);
    if (found.phone) return void phoneRef.current?.focus();
    if (found.name) return void nameRef.current?.focus();
    if (found.text) return void textRef.current?.focus();
    // Some o resultado anterior: o novo, ao entrar na região, é anunciado de novo.
    setResult(null);
    send.mutate(
      {
        lineId: selectedId,
        contactName: name.trim() === '' ? null : name.trim(),
        phone: phone.trim(),
        text: text.trim(),
      },
      {
        onSuccess: (created) => {
          setResult(created);
          // Quem simula costuma mandar várias mensagens do mesmo cliente: só o texto é limpo.
          setText('');
          textRef.current?.focus();
        },
        onError: (error) =>
          setFormError(
            simulationError(error, 'Não foi possível simular a mensagem. Tente de novo.'),
          ),
      },
    );
  };

  return (
    <div className={styles.block}>
      <h4 id={headingId} className={styles.blockTitle}>
        Simular mensagem de cliente
      </h4>
      <form
        aria-labelledby={headingId}
        className={forms.form}
        noValidate
        aria-busy={pending || undefined}
        onSubmit={submit}
      >
        <SelectField
          label="Número que recebe"
          value={selectedId}
          onChange={(event) => setLineId(event.target.value)}
        >
          {lines.map((line) => (
            <option key={line.id} value={line.id}>
              {lineNameWithPhone(line)}
            </option>
          ))}
        </SelectField>
        <Field
          ref={nameRef}
          label="Nome do cliente (opcional)"
          autoComplete="off"
          value={name}
          readOnly={pending}
          error={errors.name}
          onChange={(event) => setName(event.target.value)}
        />
        <Field
          ref={phoneRef}
          label="Telefone do cliente"
          hint="Com DDI e DDD, por exemplo +55 11 98888-7777."
          type="tel"
          inputMode="tel"
          autoComplete="off"
          value={phone}
          readOnly={pending}
          error={errors.phone}
          onChange={(event) => setPhone(event.target.value)}
        />
        <TextAreaField
          ref={textRef}
          label="Mensagem do cliente"
          rows={3}
          value={text}
          readOnly={pending}
          error={errors.text}
          onChange={(event) => setText(event.target.value)}
        />
        {formError && (
          <p role="alert" className={forms.alert}>
            {formError}
          </p>
        )}
        <div className={forms.actions}>
          <Button type="submit" variant="primary" busy={pending}>
            {pending ? 'Enviando…' : 'Simular mensagem'}
          </Button>
        </div>
        {/* Região sempre presente (vazia, sai do fluxo): só o que entra nela é anunciado. */}
        <div role="status" className={styles.result}>
          {result && (
            <>
              <span>Mensagem de cliente simulada.</span>{' '}
              <Link to={`/c/${encodeURIComponent(result.conversationId)}`} className={styles.link}>
                Abrir a conversa
              </Link>
            </>
          )}
        </div>
      </form>
    </div>
  );
}

function SeedBlock(): ReactElement {
  const queryClient = useQueryClient();
  const headingId = useId();
  const status = useStatusMessage();
  const [error, setError] = useState<string | null>(null);

  const seed = useMutation({
    mutationFn: seedSimulation,
    // As conversas novas entram nas listas (os eventos de tempo real também as trariam, mas não há
    // por que depender disso aqui).
    onSuccess: () => void invalidateConversationLists(queryClient),
  });

  return (
    <div className={styles.block}>
      <h4 id={headingId} className={styles.blockTitle}>
        Conversas de exemplo
      </h4>
      <p className={forms.hint}>
        Cria algumas conversas de exemplo nos números ligados. Pode repetir: as que já existem não
        são duplicadas.
      </p>
      <div className={forms.actions}>
        <Button
          busy={seed.isPending}
          onClick={() => {
            if (seed.isPending) return;
            setError(null);
            status.announce('');
            seed.mutate(undefined, {
              onSuccess: ({ conversations }) => status.announce(seedSentence(conversations)),
              onError: (failure) =>
                setError(
                  simulationError(failure, 'Não foi possível gerar as conversas. Tente de novo.'),
                ),
            });
          }}
        >
          {seed.isPending ? 'Gerando…' : 'Gerar conversas de exemplo'}
        </Button>
      </div>
      <StatusRegion message={status.message} />
      {error && (
        <p role="alert" className={forms.alert}>
          {error}
        </p>
      )}
    </div>
  );
}
